import { AlertTriangle, Award, BarChart3, CheckCircle2, ChevronsRight, Copy, Delete, Download, Eye, Flame, Gem, Heart, HelpCircle, Image as ImageIcon, Instagram, ListOrdered, MessageCircle, Palette, Play, Rocket, Settings, Share2, ShieldCheck, ShoppingBag, Sparkles, Trophy, User, Volume2, X } from "lucide-react";
import type { CSSProperties, Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAdsAdapter, isNativeRuntime } from "../game/ads";
import { getBackend, LeaderboardRow } from "../game/backend";
import { DAILY_LEVEL_ORDER, DAILY_LEVELS, getDailyAnswer, getLocalPuzzleDate, getNextReset, getPuzzleNumber } from "../game/daily";
import {
  createGame,
  DailyLevel,
  EMPTY_STATS,
  evaluateGuess,
  getDailyScore,
  getDailyPlayKey,
  getElapsedSeconds,
  getMaxGuesses,
  GameState,
  getKeyboardStates,
  getKeyboardStatesFromEvaluations,
  getShareText,
  Stats,
  updateStats
} from "../game/engine";
import { webPlatform } from "../game/platform";
import { getPurchaseAdapter, PRODUCT_CATALOG, PurchaseProduct, PurchaseProductId } from "../game/purchases";
import { ANSWERS } from "../game/words";

type Modal = "help" | "stats" | "settings" | "shop" | "leaders" | "share" | "profile" | "launch" | null;
type AppScreen = "menu" | "game";
type PlayMode = "daily" | "unlimited";

interface SettingsState {
  sound: boolean;
  haptics: boolean;
  colorblind: boolean;
  highContrast: boolean;
}

const DEFAULT_SETTINGS: SettingsState = {
  sound: true,
  haptics: true,
  colorblind: false,
  highContrast: false
};

const KEY_ROWS = ["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"];
const SHARE_REWARD = 20;
const REWARDED_NEXUS = 20;
const DAILY_AD_REWARD_LIMIT = 3;
const MAX_UNLIMITED_LIVES = 1;
const UNLIMITED_LIFE_RECHARGE_MS = 45 * 60 * 1000;
const SHARE_URL = import.meta.env.VITE_LEXI_SHARE_URL || "https://lexilink.game";
const TESTER_MODE = import.meta.env.DEV || import.meta.env.VITE_TESTER_MODE === "true";
const AVATAR_COLORS = ["#fbbf24", "#fb7185", "#2dd4bf", "#a3e635", "#c084fc", "#60a5fa"];

const SHOP_ITEMS = [
  {
    id: "hint-pack",
    productId: "hint_pack_small",
    title: "Hint Pack",
    description: PRODUCT_CATALOG.hint_pack_small.description,
    price: 25,
    kind: "consumable",
    icon: Sparkles
  },
  {
    id: "life-refill",
    productId: "life_refill",
    title: "3 Lives",
    description: PRODUCT_CATALOG.life_refill.description,
    price: 100,
    kind: "consumable",
    icon: Heart
  },
  {
    id: "starter-bundle",
    productId: "starter_bundle",
    title: "Starter Bundle",
    description: PRODUCT_CATALOG.starter_bundle.description,
    price: 120,
    kind: "consumable",
    icon: ShoppingBag
  },
  {
    id: "streak-shield",
    productId: "streak_shield",
    title: "Streak Shield",
    description: PRODUCT_CATALOG.streak_shield.description,
    price: 60,
    kind: "consumable",
    icon: ShieldCheck
  },
  {
    id: "theme-candy",
    productId: "theme_candy_pop",
    title: "Candy Pop",
    description: PRODUCT_CATALOG.theme_candy_pop.description,
    price: 75,
    kind: "theme",
    icon: Palette
  },
  {
    id: "theme-forest",
    productId: "theme_forest_glow",
    title: "Forest Glow",
    description: PRODUCT_CATALOG.theme_forest_glow.description,
    price: 75,
    kind: "theme",
    icon: Palette
  },
  {
    id: "ad-free",
    productId: "ad_free_pass",
    title: "Ad-Free Pass",
    description: PRODUCT_CATALOG.ad_free_pass.description,
    price: 180,
    kind: "unlock",
    icon: Gem
  }
] as const satisfies ReadonlyArray<{
  id: string;
  productId: PurchaseProductId;
  title: string;
  description: string;
  price: number;
  kind: "consumable" | "theme" | "unlock";
  icon: typeof Sparkles;
}>;

const MOCK_DAILY_STREAKS = [
  { name: "Maya", streak: 18, wins: 148, score: 18, guesses: 2 },
  { name: "Jules", streak: 12, wins: 103, score: 12, guesses: 3 },
  { name: "Rin", streak: 9, wins: 91, score: 9, guesses: 4 },
  { name: "Noah", streak: 7, wins: 77, score: 7, guesses: 4 },
  { name: "Ari", streak: 4, wins: 52, score: 4, guesses: 5 }
];

const MOCK_UNLIMITED_STAGES = [
  { name: "Maya", streak: 0, wins: 86, score: 23, guesses: 0 },
  { name: "Jules", streak: 0, wins: 71, score: 19, guesses: 0 },
  { name: "Rin", streak: 0, wins: 64, score: 17, guesses: 0 },
  { name: "Noah", streak: 0, wins: 49, score: 13, guesses: 0 },
  { name: "Ari", streak: 0, wins: 35, score: 10, guesses: 0 }
];

const EMPTY_LEADERBOARDS = {
  daily: [] as LeaderboardRow[],
  allTime: [] as LeaderboardRow[]
};

type RewardedClaimKind = "hint" | "nexus";

interface DailyGoal {
  id: string;
  claimKey: string;
  title: string;
  detail: string;
  reward: number;
  complete: boolean;
  claimed: boolean;
}

function getViewportSize() {
  const visual = window.visualViewport;
  const widths = [visual?.width, window.innerWidth, document.documentElement.clientWidth, window.screen.width]
    .filter((value): value is number => typeof value === "number" && value > 0)
    .map(Math.floor);
  const heights = [visual?.height, window.innerHeight, document.documentElement.clientHeight, window.screen.height]
    .filter((value): value is number => typeof value === "number" && value > 0)
    .map(Math.floor);
  return {
    width: Math.min(...widths),
    height: Math.min(...heights)
  };
}

function useViewportSize() {
  const [viewport, setViewport] = useState(getViewportSize);

  useEffect(() => {
    const update = () => setViewport(getViewportSize());
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  return viewport;
}

function getBoardMetrics(rows: number, wordLength: number, viewport: { width: number; height: number }, isComplete = false) {
  const compact = viewport.height <= 700;
  const isExpertBoard = rows === 5 && wordLength === 6;
  const gap = rows === 7 || isExpertBoard ? 3 : 4;
  const sixLetterMaxTile = viewport.width <= 340 ? 36 : viewport.width <= 380 ? 39 : viewport.height >= 900 ? 42 : 40;
  const maxTile = isComplete ? (rows >= 6 ? 38 : 42) : rows === 7 ? (viewport.height >= 900 ? 46 : 42) : wordLength === 6 ? sixLetterMaxTile : 50;
  const baseReservedHeight = isComplete
    ? viewport.height <= 700
      ? 420
      : 400
    : compact
      ? rows === 7
        ? 340
        : rows === 5 && wordLength === 6
          ? 304
          : 300
      : rows === 7
        ? 312
        : rows === 5 && wordLength === 6
          ? 286
          : wordLength === 6
          ? 272
          : 258;
  const reservedHeight = baseReservedHeight + (isComplete ? 0 : 36);
  const horizontalPadding = wordLength === 6 ? 64 : 54;
  const horizontalTile = Math.floor((viewport.width - horizontalPadding - gap * (wordLength - 1)) / wordLength);
  const verticalTile = Math.floor((viewport.height - reservedHeight - gap * (rows - 1)) / rows);
  const minTile = isComplete ? 24 : wordLength === 6 ? 28 : 32;
  const tileSize = Math.max(minTile, Math.min(maxTile, horizontalTile, verticalTile));
  return {
    gap,
    tileSize,
    width: wordLength * tileSize + gap * (wordLength - 1)
  };
}

function getRewardClaims(stats: Stats, dateKey: string) {
  return stats.adRewardClaims?.[dateKey] ?? { hint: 0, nexus: 0 };
}

function getRewardRemaining(stats: Stats, dateKey: string, kind: RewardedClaimKind) {
  const claims = getRewardClaims(stats, dateKey);
  return Math.max(0, DAILY_AD_REWARD_LIMIT - (claims[kind] ?? 0));
}

function recordRewardClaim(stats: Stats, dateKey: string, kind: RewardedClaimKind): Stats {
  const currentClaims = getRewardClaims(stats, dateKey);
  return {
    ...stats,
    adRewardClaims: {
      ...(stats.adRewardClaims ?? EMPTY_STATS.adRewardClaims),
      [dateKey]: {
        ...currentClaims,
        [kind]: (currentClaims[kind] ?? 0) + 1
      }
    }
  };
}

function getRandomAnswer(level: DailyLevel) {
  const answers = DAILY_LEVELS[level]?.answers ?? ANSWERS;
  return answers[Math.floor(Math.random() * answers.length)];
}

function createUnlimitedGame(level: DailyLevel) {
  return createGame(`unlimited-${level}-${Date.now()}`, getRandomAnswer(level), "unlimited", level);
}

function lifeWord(count: number) {
  return count === 1 ? "life" : "lives";
}

function formatLifeBalance(lives: number) {
  return `${lives} ${lifeWord(lives)}`;
}

function formatLifeHearts(lives: number) {
  if (lives <= 0) return "♡";
  if (lives <= 3) return "♥".repeat(lives);
  return `♥ x${lives}`;
}

function normalizeUnlimitedLivesForCurrentRules(stats: Stats): Stats {
  const lives = stats.unlimitedLives ?? EMPTY_STATS.unlimitedLives;
  const hasPaidLifePack = (stats.purchaseHistory ?? EMPTY_STATS.purchaseHistory).some((purchase) => purchase.productId === "life_refill");
  if (lives <= MAX_UNLIMITED_LIVES || hasPaidLifePack) return stats;
  return { ...stats, unlimitedLives: MAX_UNLIMITED_LIVES, unlimitedLifeUpdatedAt: Date.now() };
}

function getUnlimitedLifeState(stats: Stats, now = Date.now()) {
  const currentLives = Math.max(0, stats.unlimitedLives ?? EMPTY_STATS.unlimitedLives);
  const updatedAt = stats.unlimitedLifeUpdatedAt ?? now;

  if (currentLives >= MAX_UNLIMITED_LIVES) {
    return { lives: currentLives, updatedAt: now, nextLifeAt: null as number | null, nextLifeText: "Full" };
  }

  const earnedLives = Math.max(0, Math.floor((now - updatedAt) / UNLIMITED_LIFE_RECHARGE_MS));
  const lives = Math.min(MAX_UNLIMITED_LIVES, currentLives + earnedLives);
  const nextUpdatedAt = earnedLives > 0 ? updatedAt + earnedLives * UNLIMITED_LIFE_RECHARGE_MS : updatedAt;

  if (lives >= MAX_UNLIMITED_LIVES) {
    return { lives, updatedAt: now, nextLifeAt: null as number | null, nextLifeText: "Full" };
  }

  const nextLifeAt = nextUpdatedAt + UNLIMITED_LIFE_RECHARGE_MS;
  const remainingMs = Math.max(0, nextLifeAt - now);
  const minutes = Math.ceil(remainingMs / 60000);
  return {
    lives,
    updatedAt: nextUpdatedAt,
    nextLifeAt,
    nextLifeText: `${minutes}m`
  };
}

function rechargeUnlimitedLives(stats: Stats, now = Date.now()): Stats {
  const lifeState = getUnlimitedLifeState(stats, now);
  return {
    ...stats,
    unlimitedLives: lifeState.lives,
    unlimitedLifeUpdatedAt: lifeState.updatedAt
  };
}

function refillUnlimitedLives(stats: Stats): Stats {
  return {
    ...stats,
    unlimitedLives: MAX_UNLIMITED_LIVES,
    unlimitedLifeUpdatedAt: Date.now()
  };
}

function spendUnlimitedLife(stats: Stats): Stats {
  const now = Date.now();
  const lifeState = getUnlimitedLifeState(stats, now);
  return {
    ...stats,
    unlimitedLives: Math.max(0, lifeState.lives - 1),
    unlimitedLifeUpdatedAt: lifeState.lives >= MAX_UNLIMITED_LIVES ? now : lifeState.updatedAt
  };
}

function recordUnlimitedResult(stats: Stats, status: GameState["status"]): Stats {
  const recharged = rechargeUnlimitedLives(stats);

  if (status === "won") {
    const nextRun = (recharged.unlimitedCurrentRun ?? EMPTY_STATS.unlimitedCurrentRun) + 1;
    return {
      ...recharged,
      unlimitedWins: (recharged.unlimitedWins ?? EMPTY_STATS.unlimitedWins) + 1,
      unlimitedCurrentRun: nextRun,
      unlimitedBestRun: Math.max(recharged.unlimitedBestRun ?? EMPTY_STATS.unlimitedBestRun, nextRun),
      nexus: (recharged.nexus ?? EMPTY_STATS.nexus) + 10
    };
  }

  if (status === "lost") {
    return {
      ...spendUnlimitedLife(recharged),
      unlimitedCurrentRun: 0,
      nexus: (recharged.nexus ?? EMPTY_STATS.nexus) + 2
    };
  }

  return recharged;
}

function scoreGuessLocally(game: GameState, guess: string): Partial<GameState> {
  const answer = game.answer || getDailyAnswer(game.dateKey, game.level);
  const guesses = [...game.guesses, guess];
  const evaluations = [...(game.evaluations ?? []), evaluateGuess(guess, answer).map((item) => item.state)];
  const status = guess === answer ? "won" : guesses.length >= getMaxGuesses(game.level) ? "lost" : "playing";

  return {
    guesses,
    evaluations,
    status,
    answer,
    completedAt: status === "playing" ? undefined : Date.now()
  };
}

function getDailyGoals(game: GameState, stats: Stats, shareRewardDates: string[]): DailyGoal[] {
  const dailyPlayKey = getDailyPlayKey(game);
  const claimedKeys = stats.dailyGoalClaims ?? EMPTY_STATS.dailyGoalClaims;
  const goalBase = game.mode === "daily" ? dailyPlayKey : game.dateKey;
  const completed = game.mode === "daily" && game.status !== "playing";
  const won = game.mode === "daily" && game.status === "won";
  const shared = game.mode === "daily" && shareRewardDates.includes(dailyPlayKey);

  const goals = [
    {
      id: "finish",
      title: "Finish a daily",
      detail: "Complete this level",
      reward: 10,
      complete: completed
    },
    {
      id: "win",
      title: "Win the board",
      detail: "Solve before you run out of tries",
      reward: 15,
      complete: won
    },
    {
      id: "clean",
      title: "Clean solve",
      detail: "Win without using a hint",
      reward: 10,
      complete: won && game.hintsUsed === 0
    },
    {
      id: "share",
      title: "Share the run",
      detail: "Share or copy today’s result",
      reward: 10,
      complete: shared
    }
  ];

  return goals.map((goal) => {
    const claimKey = `${goalBase}:${goal.id}`;
    return {
      ...goal,
      claimKey,
      claimed: claimedKeys.includes(claimKey)
    };
  });
}

function getAchievements(stats: Stats, game: GameState) {
  return [
    { id: "first-win", title: "First Link", detail: "Win one daily puzzle", unlocked: stats.wins > 0 },
    { id: "streak-3", title: "Warm Streak", detail: "Hold a 3-day streak", unlocked: stats.currentStreak >= 3 },
    { id: "streak-7", title: "Week Weaver", detail: "Hold a 7-day streak", unlocked: stats.currentStreak >= 7 },
    { id: "quick", title: "Sharp Solve", detail: "Solve in 3 guesses or fewer", unlocked: game.status === "won" && game.guesses.length <= 3 },
    { id: "no-hint", title: "Clean Read", detail: "Solve without a hint", unlocked: game.status === "won" && game.hintsUsed === 0 },
    { id: "runner", title: "Link Run", detail: "Win 5 Unlimited puzzles", unlocked: (stats.unlimitedWins ?? 0) >= 5 },
    { id: "shielded", title: "Safety Net", detail: "Keep at least one Streak Shield", unlocked: (stats.streakShields ?? 0) > 0 },
    { id: "collector", title: "Perk Collector", detail: "Own 3 shop perks", unlocked: (stats.ownedPerks ?? []).length >= 3 }
  ];
}

function getDateGap(previousDate: string | undefined, currentDate: string) {
  if (!previousDate) return 0;
  const previous = Date.parse(`${previousDate}T00:00:00Z`);
  const current = Date.parse(`${currentDate}T00:00:00Z`);
  if (Number.isNaN(previous) || Number.isNaN(current)) return 1;
  return Math.round((current - previous) / 86400000);
}

function getVisibleDailyStreak(stats: Stats, currentDateKey: string) {
  if (!stats.lastStreakDate) return 0;
  return getDateGap(stats.lastStreakDate, currentDateKey) <= 1 ? stats.currentStreak : 0;
}

function getLaunchChecklist(backendOnline: boolean) {
  return [
    {
      title: "Firebase project",
      detail: backendOnline ? "Live backend detected for leaderboards and saves." : "Add Firebase env vars and deploy rules/functions.",
      done: backendOnline
    },
    {
      title: "Firebase App Check",
      detail: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ? "Web App Check site key is configured." : "Add App Check providers and set the web site key.",
      done: Boolean(import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY)
    },
    {
      title: "RevenueCat keys",
      detail:
        import.meta.env.VITE_REVENUECAT_IOS_API_KEY && import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY
          ? "iOS and Android RevenueCat keys are present."
          : "Add RevenueCat iOS and Android public SDK keys.",
      done: Boolean(import.meta.env.VITE_REVENUECAT_IOS_API_KEY && import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY)
    },
    {
      title: "Store products",
      detail: "Create all seven product IDs in Apple, Google, and RevenueCat.",
      done: false
    },
    {
      title: "AdMob revenue",
      detail: "Native ad adapter is wired. Add real AdMob app IDs, ad units, and consent messages.",
      done: Boolean(import.meta.env.VITE_ADMOB_ANDROID_REWARDED_ID && import.meta.env.VITE_ADMOB_IOS_REWARDED_ID)
    },
    {
      title: "Android release",
      detail: "Install JDK/Android Studio, then build a signed AAB for internal testing.",
      done: false
    },
    {
      title: "iOS release",
      detail: "On macOS/Xcode, enable In-App Purchase and archive for TestFlight.",
      done: false
    },
    {
      title: "Policy pages",
      detail: "Local privacy/support pages and form drafts are ready. Publish them to the production domain.",
      done: true
    }
  ];
}

function getDailyRows(game: GameState, stats: Stats, backendRows: LeaderboardRow[] = [], currentDateKey = game.dateKey) {
  if (backendRows.length > 0) {
    const playerName = stats.profileName || "You";
    const visibleStreak = getVisibleDailyStreak(stats, currentDateKey);
    const hasPlayerRow = backendRows.some((row) => row.name === playerName);
    const rows = hasPlayerRow
      ? backendRows
      : [
          ...backendRows,
          {
            name: playerName,
            streak: visibleStreak,
            wins: stats.wins,
            score: visibleStreak,
            guesses: game.status === "won" ? game.guesses.length : 0
          }
        ];
    return rows.map((row) => ({ ...row, score: row.streak })).sort((a, b) => b.streak - a.streak || b.wins - a.wins);
  }
  const activeStreak = getVisibleDailyStreak(stats, currentDateKey);
  const playerRow = {
    name: stats.profileName || "You",
    streak: activeStreak,
    wins: stats.wins,
    score: activeStreak,
    guesses: game.status === "won" ? game.guesses.length : 0
  };
  return [...MOCK_DAILY_STREAKS, playerRow].sort((a, b) => b.streak - a.streak || b.wins - a.wins);
}

function getUnlimitedRows(stats: Stats, backendRows: LeaderboardRow[] = []) {
  if (backendRows.length > 0) return backendRows.sort((a, b) => b.score - a.score);
  const playerRow = {
    name: stats.profileName || "You",
    streak: 0,
    wins: stats.unlimitedWins ?? EMPTY_STATS.unlimitedWins,
    score: stats.unlimitedBestRun ?? EMPTY_STATS.unlimitedBestRun,
    guesses: 0
  };
  return [...MOCK_UNLIMITED_STAGES, playerRow].sort((a, b) => b.score - a.score || b.wins - a.wins);
}

function getGridEmojiRows(game: GameState) {
  return game.guesses.map((guess, rowIndex) => {
    const states = game.evaluations?.[rowIndex] ?? evaluateGuess(guess, game.answer).map((item) => item.state);
    return states
      .map((state) => {
        if (state === "correct") return "🟩";
        if (state === "present") return "🟨";
        return "⬛";
      })
      .join("");
  });
}

function makeShareCard(game: GameState, stats: Stats, puzzleNumber: number) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, "#fb923c");
  gradient.addColorStop(0.42, "#6b2437");
  gradient.addColorStop(1, "#28131b");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = "rgba(255, 247, 237, 0.08)";
  ctx.beginPath();
  ctx.arc(870, 140, 210, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(160, 1160, 260, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "#fff7ed";
  ctx.textAlign = "center";
  ctx.font = "900 86px Arial";
  ctx.fillText("Lexi Link", 540, 145);
  ctx.font = "700 34px Arial";
  ctx.fillStyle = "#fed7aa";
  ctx.fillText(game.mode === "daily" ? `Daily #${puzzleNumber}` : "Unlimited Run", 540, 195);

  ctx.fillStyle = stats.avatarColor ?? EMPTY_STATS.avatarColor;
  ctx.beginPath();
  ctx.arc(540, 280, 44, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fff7ed";
  ctx.font = "900 48px Arial";
  ctx.fillText(stats.profileName || "Player", 540, 365);

  const score = game.mode === "daily" ? getDailyScore(game, stats.currentStreak) : game.status === "won" ? 100 + (stats.unlimitedCurrentRun ?? 0) * 25 : 0;
  const maxGuesses = getMaxGuesses(game.level);
  const result = game.status === "won" ? `${game.guesses.length}/${maxGuesses}` : `X/${maxGuesses}`;
  ctx.font = "900 58px Arial";
  ctx.fillStyle = "#fde68a";
  ctx.fillText(`${result} · ${score} pts`, 540, 450);
  ctx.font = "700 32px Arial";
  ctx.fillStyle = "#ffedd5";
  ctx.fillText(
    game.mode === "daily"
      ? `${stats.currentStreak} streak · ${stats.wins} wins`
      : `${stats.unlimitedCurrentRun ?? 0} run · ${stats.unlimitedWins ?? 0} unlimited wins`,
    540,
    498
  );

  const wordLength = DAILY_LEVELS[game.level].wordLength;
  const tileSize = wordLength > 5 ? 80 : 92;
  const gap = wordLength > 5 ? 11 : 14;
  const boardWidth = tileSize * wordLength + gap * (wordLength - 1);
  const startX = (canvas.width - boardWidth) / 2;
  const startY = 540;
  const statesByRow = game.guesses.map((guess, rowIndex) =>
    game.evaluations?.[rowIndex] ?? evaluateGuess(guess, game.answer).map((item) => item.state)
  );

  for (let row = 0; row < maxGuesses; row += 1) {
    for (let col = 0; col < wordLength; col += 1) {
      const x = startX + col * (tileSize + gap);
      const y = startY + row * (tileSize + gap);
      const state = statesByRow[row]?.[col];
      ctx.fillStyle = state === "correct" ? "#12946f" : state === "present" ? "#f59e0b" : state === "absent" ? "#5b3440" : "rgba(43, 23, 32, 0.72)";
      ctx.strokeStyle = state ? "rgba(255,247,237,.55)" : "rgba(255,247,237,.22)";
      ctx.lineWidth = 5;
      roundRect(ctx, x, y, tileSize, tileSize, 20);
      ctx.fill();
      ctx.stroke();
      const letter = game.guesses[row]?.[col];
      if (letter) {
        ctx.fillStyle = state === "present" ? "#2b1008" : "#fff7ed";
        ctx.font = "900 54px Arial";
        ctx.fillText(letter, x + tileSize / 2, y + 62);
      }
    }
  }

  ctx.fillStyle = "#fff7ed";
  ctx.font = "800 34px Arial";
  ctx.fillText("Share your score. Start a streak.", 540, 1188);
  ctx.fillStyle = "#fed7aa";
  ctx.font = "700 30px Arial";
  ctx.fillText("Play Lexi Link · lexilink.game", 540, 1262);

  return canvas.toDataURL("image/png");
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function applyProductGrant(stats: Stats, product: PurchaseProduct): Stats {
  const currentOwned = stats.ownedPerks ?? EMPTY_STATS.ownedPerks;
  const perkId = product.grant.perkId;
  return {
    ...stats,
    nexus: (stats.nexus ?? EMPTY_STATS.nexus) + (product.grant.nexus ?? 0),
    hints: (stats.hints ?? EMPTY_STATS.hints) + (product.grant.hints ?? 0),
    streakShields: (stats.streakShields ?? EMPTY_STATS.streakShields) + (product.grant.streakShields ?? 0),
    unlimitedLives: product.grant.unlimitedLives
      ? (stats.unlimitedLives ?? EMPTY_STATS.unlimitedLives) + product.grant.unlimitedLives
      : stats.unlimitedLives ?? EMPTY_STATS.unlimitedLives,
    unlimitedLifeUpdatedAt: product.grant.unlimitedLives ? Date.now() : stats.unlimitedLifeUpdatedAt,
    ownedPerks: perkId ? [...new Set([...currentOwned, perkId])] : currentOwned,
    activeTheme: product.grant.activeTheme ?? stats.activeTheme ?? EMPTY_STATS.activeTheme
  };
}

function applyPurchaseGrant(stats: Stats, product: PurchaseProduct, transactionId: string, platform: string): Stats {
  const granted = applyProductGrant(stats, product);
  return {
    ...granted,
    purchaseHistory: [
      ...(granted.purchaseHistory ?? EMPTY_STATS.purchaseHistory),
      {
        productId: product.id,
        transactionId,
        platform,
        purchasedAt: Date.now()
      }
    ]
  };
}

export function App() {
  const viewport = useViewportSize();
  const [dateKey] = useState(getLocalPuzzleDate);
  const [game, setGame] = useState<GameState>(() => createGame(dateKey, getDailyAnswer(dateKey, "classic"), "daily", "classic"));
  const [stats, setStats] = useState<Stats>(EMPTY_STATS);
  const [settings, setSettings] = useState<SettingsState>(DEFAULT_SETTINGS);
  const [modal, setModal] = useState<Modal>(null);
  const [toast, setToast] = useState("");
  const [invalidRow, setInvalidRow] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [screen, setScreen] = useState<AppScreen>("menu");
  const [selectedMode, setSelectedMode] = useState<PlayMode>("daily");
  const [savedGamesCache, setSavedGamesCache] = useState<Record<string, GameState>>({});
  const [leaderboards, setLeaderboards] = useState(EMPTY_LEADERBOARDS);
  const [backendOnline, setBackendOnline] = useState(false);
  const [activeLevel, setActiveLevel] = useState<DailyLevel>("classic");
  const [rewardBusy, setRewardBusy] = useState<RewardedClaimKind | null>(null);
  const [now, setNow] = useState(Date.now);
  const [stageTransition, setStageTransition] = useState<{ level: DailyLevel; stage: number } | null>(null);
  const submittedResultKeys = useRef(new Set<string>());
  const sessionStartKeys = useRef(new Set<string>());
  const interstitialKeys = useRef(new Set<string>());
  const toastTimer = useRef<number | undefined>(undefined);
  const stageTimer = useRef<number | undefined>(undefined);

  const puzzleNumber = getPuzzleNumber(dateKey);
  const currentLevel = screen === "menu" ? activeLevel : game.level ?? activeLevel;
  const levelConfig = DAILY_LEVELS[currentLevel];
  const maxRows = levelConfig.tries;
  const visibleRows = maxRows;
  const wordLength = levelConfig.wordLength;
  const boardMetrics = useMemo(
    () => getBoardMetrics(visibleRows, wordLength, viewport, game.status !== "playing"),
    [game.status, visibleRows, viewport, wordLength]
  );
  const keyboardStates = useMemo(
    () =>
      game.evaluations?.length
        ? getKeyboardStatesFromEvaluations(game.guesses, game.evaluations)
        : getKeyboardStates(game.guesses, game.answer),
    [game.answer, game.evaluations, game.guesses]
  );
  const resetAt = useMemo(() => getNextReset(), []);
  const ownedPerks = stats.ownedPerks ?? EMPTY_STATS.ownedPerks;
  const activeTheme = stats.activeTheme ?? EMPTY_STATS.activeTheme;
  const nexusPoints = stats.nexus ?? EMPTY_STATS.nexus;
  const profileName = stats.profileName || EMPTY_STATS.profileName;
  const shareRewardDates = stats.shareRewardDates ?? EMPTY_STATS.shareRewardDates;
  const dailyPlayKey = getDailyPlayKey(game);
  const shareRewardAvailable = game.mode === "daily" && game.status !== "playing" && !shareRewardDates.includes(dailyPlayKey);
  const adFree = ownedPerks.includes("ad-free");
  const hintRewardsLeft = getRewardRemaining(stats, dateKey, "hint");
  const nexusRewardsLeft = getRewardRemaining(stats, dateKey, "nexus");
  const dailyGoals = useMemo(() => getDailyGoals(game, stats, shareRewardDates), [game, shareRewardDates, stats]);
  const unlimitedLifeState = useMemo(() => getUnlimitedLifeState(stats, now), [now, stats]);

  function getDailyGameKey(level: DailyLevel) {
    return `${dateKey}:${level}`;
  }

  function normalizeGame(saved: GameState | undefined, level: DailyLevel) {
    if (!saved) return createGame(dateKey, getDailyAnswer(dateKey, level), "daily", level);
    if (saved.mode !== "daily" || saved.level !== level || saved.dateKey !== dateKey) {
      return createGame(dateKey, getDailyAnswer(dateKey, level), "daily", level);
    }
    if ((saved.answer?.length ?? DAILY_LEVELS[level].wordLength) !== DAILY_LEVELS[level].wordLength) {
      return createGame(dateKey, getDailyAnswer(dateKey, level), "daily", level);
    }
    return {
      ...saved,
      startedAt: saved.startedAt ?? Date.now(),
      hintsUsed: saved.hintsUsed ?? 0,
      mode: saved.mode ?? "daily",
      level: saved.level ?? level
    };
  }

  useEffect(() => {
    async function loadSavedState() {
      const [savedGames, savedStats, savedSettings, savedLevel] = await Promise.all([
        webPlatform.load<Record<string, GameState>>("games", {}),
        webPlatform.load<Stats>("stats", EMPTY_STATS),
        webPlatform.load<SettingsState>("settings", DEFAULT_SETTINGS),
        webPlatform.load<DailyLevel>("dailyLevel", "classic")
      ]);
      const level = DAILY_LEVEL_ORDER.includes(savedLevel) ? savedLevel : "classic";

      let cloudStats = normalizeUnlimitedLivesForCurrentRules(savedStats);
      try {
        const backend = await getBackend();
        cloudStats = normalizeUnlimitedLivesForCurrentRules(await backend.loadStats(savedStats));
        setBackendOnline(backend.enabled);
      } catch {
        setBackendOnline(false);
      }
      setActiveLevel(level);
      setSelectedMode("daily");
      setSavedGamesCache(savedGames);
      setGame(normalizeGame(savedGames[getDailyGameKey(level)] ?? savedGames[dateKey], level));
      setStats(cloudStats);
      setSettings(savedSettings);
      setLoaded(true);
      setModal(null);
    }

    loadSavedState();
  }, [dateKey]);

  useEffect(() => {
    async function startLockedSession() {
      if (!loaded || screen !== "game" || game.mode !== "daily" || game.guesses.length > 0) return;
      const playKey = getDailyPlayKey(game);
      if (sessionStartKeys.current.has(playKey)) return;
      sessionStartKeys.current.add(playKey);
      try {
        const backend = await getBackend();
        if (!backend.enabled) return;
        const session = await backend.startDailySession(game);
        setGame((current) => ({ ...current, ...session, answer: session.answer ?? current.answer }));
      } catch {
        setBackendOnline(false);
      }
    }

    startLockedSession();
  }, [game, loaded, screen]);

  useEffect(() => {
    return () => {
      if (stageTimer.current) {
        window.clearTimeout(stageTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!loaded) return;
    setStats((current) => rechargeUnlimitedLives(current));
    const timer = window.setInterval(() => {
      setNow(Date.now());
      setStats((current) => rechargeUnlimitedLives(current));
    }, 30000);
    return () => window.clearInterval(timer);
  }, [loaded]);

  useEffect(() => {
    if (!loaded || adFree) return;
    getAdsAdapter().initialize().catch(() => undefined);
  }, [adFree, loaded]);

  useEffect(() => {
    if (!loaded) return;

    async function persist() {
      const savedGames = await webPlatform.load<Record<string, GameState>>("games", {});
      const nextGames = { ...savedGames, [game.mode === "daily" ? getDailyGameKey(game.level) : "unlimited"]: game };
      await webPlatform.save("games", nextGames);
      await webPlatform.save("stats", stats);
      await webPlatform.save("settings", settings);
      await webPlatform.save("dailyLevel", activeLevel);
      setSavedGamesCache(nextGames);
      try {
        const backend = await getBackend();
        await backend.saveStats(stats);
      } catch {
        setBackendOnline(false);
      }
    }

    persist();
  }, [activeLevel, game, loaded, settings, stats]);

  useEffect(() => {
    setStats((current) => updateStats(current, game));
  }, [game]);

  useEffect(() => {
    async function refreshLeaderboards() {
      try {
        const backend = await getBackend();
        setBackendOnline(backend.enabled);
        const [daily, allTime] = await Promise.all([backend.getDailyLeaderboard(dateKey, activeLevel), backend.getUnlimitedLeaderboard()]);
        setLeaderboards({ daily, allTime });
      } catch {
        setBackendOnline(false);
      }
    }

    refreshLeaderboards();
  }, [activeLevel, dateKey, stats.currentStreak, stats.unlimitedBestRun, stats.unlimitedWins, stats.wins]);

  useEffect(() => {
    async function submitResult() {
      if (!loaded || game.mode !== "daily" || game.status === "playing") return;
      const resultKey = `${getDailyPlayKey(game)}:${game.status}:${game.guesses.join("-")}`;
      if (submittedResultKeys.current.has(resultKey)) return;
      submittedResultKeys.current.add(resultKey);

      try {
        const backend = await getBackend();
        await backend.submitDailyResult(game, stats);
        const [daily, allTime] = await Promise.all([backend.getDailyLeaderboard(dateKey, game.level), backend.getUnlimitedLeaderboard()]);
        setLeaderboards({ daily, allTime });
      } catch {
        setBackendOnline(false);
      }
    }

    submitResult();
  }, [dateKey, game, loaded, stats]);

  useEffect(() => {
    async function submitUnlimitedLeaderboard() {
      if (!loaded || game.mode !== "unlimited" || game.status !== "won") return;
      if ((stats.unlimitedBestRun ?? 0) <= 0) return;
      const resultKey = `unlimited:${stats.unlimitedBestRun}:${stats.unlimitedWins}`;
      if (submittedResultKeys.current.has(resultKey)) return;
      submittedResultKeys.current.add(resultKey);

      try {
        const backend = await getBackend();
        await backend.submitUnlimitedResult(stats);
        const allTime = await backend.getUnlimitedLeaderboard();
        setLeaderboards((current) => ({ ...current, allTime }));
      } catch {
        setBackendOnline(false);
      }
    }

    submitUnlimitedLeaderboard();
  }, [game.mode, game.status, loaded, stats]);

  useEffect(() => {
    async function showResultInterstitial() {
      if (!loaded || adFree || game.mode !== "daily" || game.status === "playing") return;
      if (game.guesses.length < 2) return;
      const resultKey = `${getDailyPlayKey(game)}:${game.status}:interstitial`;
      if (interstitialKeys.current.has(resultKey)) return;
      interstitialKeys.current.add(resultKey);

      const adapter = getAdsAdapter();
      const init = await adapter.initialize();
      if (!init.ok) return;
      await adapter.showInterstitial();
    }

    const timer = window.setTimeout(() => {
      showResultInterstitial().catch(() => undefined);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [adFree, game.dateKey, game.guesses.length, game.mode, game.status, loaded]);

  const notify = useCallback((message: string) => {
    if (toastTimer.current) {
      window.clearTimeout(toastTimer.current);
    }
    setToast(message);
    toastTimer.current = window.setTimeout(() => {
      setToast("");
      toastTimer.current = undefined;
    }, 1800);
  }, []);

  const commitGuess = useCallback(async () => {
    if (game.status !== "playing") return;
    const guess = game.currentGuess;

    if (guess.length < wordLength) {
      setInvalidRow(true);
      window.setTimeout(() => setInvalidRow(false), 450);
      notify("Not enough letters");
      return;
    }

    const applyScoredResult = (result: Partial<GameState>, localMessage?: string) => {
      const status = result.status ?? "playing";
      if (settings.haptics) {
        webPlatform.vibrate(status === "won" ? [35, 25, 35] : 12);
      }
      setGame((current) => ({
        ...current,
        ...result,
        answer: result.answer ?? current.answer,
        currentGuess: "",
        completedAt: status === "playing" ? undefined : result.completedAt ?? Date.now()
      }));
      if (game.mode === "unlimited" && status !== "playing") {
        setStats((current) => recordUnlimitedResult(current, status));
        notify(status === "won" ? "+10 Nexus. Keep the run going!" : "Life lost. Try another word.");
      } else if (localMessage) {
        notify(localMessage);
      }
    };

    if (game.mode !== "daily") {
      applyScoredResult(scoreGuessLocally(game, guess));
      return;
    }

    try {
      const backend = await getBackend();
      const result = await backend.submitGuess(game, guess);
      applyScoredResult(result);
    } catch {
      setBackendOnline(false);
      applyScoredResult(scoreGuessLocally(game, guess), "Scored locally");
    }
  }, [game, notify, settings.haptics, wordLength]);

  const pressKey = useCallback(
    (key: string) => {
      const normalized = key.toUpperCase();

      if (normalized === "ENTER") {
        commitGuess();
        return;
      }

      if (normalized === "BACKSPACE") {
        setGame((current) =>
          current.status === "playing" ? { ...current, currentGuess: current.currentGuess.slice(0, -1) } : current
        );
        return;
      }

      if (/^[A-Z]$/.test(normalized)) {
        setGame((current) => {
          if (current.status !== "playing") return current;
          const currentWordLength = DAILY_LEVELS[current.level].wordLength;
          if (current.currentGuess.length >= currentWordLength) {
            notify("Tap Check to score this word");
            return current;
          }
          if (settings.haptics) webPlatform.vibrate(8);
          return { ...current, currentGuess: current.currentGuess + normalized };
        });
      }
    },
    [commitGuess, notify, settings.haptics]
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (screen !== "game") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "Enter" || event.key === "Backspace" || /^[a-zA-Z]$/.test(event.key)) {
        event.preventDefault();
        pressKey(event.key);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pressKey, screen]);

  async function shareResult() {
    const text = getShareText(game, puzzleNumber);
    const shareText = `${profileName}'s result\n${text}\n\nPlay Lexi Link and compare your score. ${SHARE_URL}`;
    const awardShareReward = () => {
      if (!shareRewardAvailable) return false;
      setStats((current) => ({
        ...current,
        nexus: (current.nexus ?? EMPTY_STATS.nexus) + SHARE_REWARD,
        shareRewardDates: [...new Set([...(current.shareRewardDates ?? EMPTY_STATS.shareRewardDates), dailyPlayKey])]
      }));
      return true;
    };

    try {
      await webPlatform.share(shareText);
      if (awardShareReward()) {
        notify(`Shared! +${SHARE_REWARD} Nexus`);
      } else {
        notify("Result shared");
      }
    } catch {
      try {
        await navigator.clipboard.writeText(shareText);
        if (awardShareReward()) {
          notify(`Copied! +${SHARE_REWARD} Nexus`);
        } else {
          notify("Result copied");
        }
      } catch {
        if (awardShareReward()) {
          notify(`Share ready! +${SHARE_REWARD} Nexus`);
        } else {
          notify("Share ready");
        }
      }
    }
  }

  async function copyShareText(action = "Result copied") {
    const text = getShareText(game, puzzleNumber);
    const shareText = `${profileName}'s result\n${text}\n\nPlay Lexi Link and compare your score. ${SHARE_URL}`;
    try {
      await navigator.clipboard.writeText(shareText);
      notify(action);
      return true;
    } catch {
      notify("Share text ready");
      return false;
    }
  }

  async function shareToNetwork(network: string, url: string) {
    await copyShareText(`${network} share ready`);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function claimDailyGoal(goal: DailyGoal) {
    if (!goal.complete) {
      notify("Finish the goal first");
      return;
    }
    if (goal.claimed) {
      notify("Goal already claimed");
      return;
    }

    setStats((current) => ({
      ...current,
      nexus: (current.nexus ?? EMPTY_STATS.nexus) + goal.reward,
      dailyGoalClaims: [...new Set([...(current.dailyGoalClaims ?? EMPTY_STATS.dailyGoalClaims), goal.claimKey])]
    }));
    notify(`${goal.title}: +${goal.reward} Nexus`);
  }

  function revealHint() {
    if (game.hint) {
      setModal(null);
      notify(game.hint);
      return;
    }
    if (game.status !== "playing") {
      notify("Hints are only available while playing");
      return;
    }
    if ((stats.hints ?? EMPTY_STATS.hints) <= 0) {
      setModal("shop");
      notify("Buy hints in the shop");
      return;
    }
    const hiddenIndexes = game.answer
      .split("")
      .map((letter, index) => ({ letter, index }))
      .filter(({ letter, index }) => game.guesses.every((guess) => guess[index] !== letter));
    const hint = hiddenIndexes[0] ?? { letter: game.answer[0], index: 0 };
    const hintText = `${hint.letter} is in position ${hint.index + 1}`;
    setGame((current) => ({ ...current, hint: hintText, hintsUsed: current.hintsUsed + 1 }));
    setStats((current) => ({ ...current, hints: Math.max(0, (current.hints ?? EMPTY_STATS.hints) - 1) }));
    setModal(null);
    notify(hintText);
  }

  function normalizeUnlimitedGame(saved: GameState | undefined, level: DailyLevel) {
    const config = DAILY_LEVELS[level] ?? DAILY_LEVELS.classic;
    if (!saved || saved.mode === "daily" || saved.level !== level || saved.status !== "playing") {
      return createUnlimitedGame(level);
    }
    if ((saved.answer?.length ?? config.wordLength) !== config.wordLength) {
      return createUnlimitedGame(level);
    }
    return {
      ...saved,
      mode: "unlimited" as const,
      level,
      startedAt: saved.startedAt ?? Date.now(),
      hintsUsed: saved.hintsUsed ?? 0
    };
  }

  async function watchRewardedHint() {
    if (rewardBusy) return;
    if (hintRewardsLeft <= 0) {
      notify("Hint rewards are done for today");
      return;
    }

    setRewardBusy("hint");
    if (adFree) {
      setStats((current) => {
        const next = recordRewardClaim(current, dateKey, "hint");
        return { ...next, hints: (next.hints ?? EMPTY_STATS.hints) + 1 };
      });
      notify(`Ad-free bonus: +1 hint · ${Math.max(0, hintRewardsLeft - 1)} left`);
      setRewardBusy(null);
      return;
    }

    notify("Starting rewarded ad...");
    try {
      const adapter = getAdsAdapter();
      const init = await adapter.initialize();
      if (!init.ok) {
        setStats((current) => {
          const next = recordRewardClaim(current, dateKey, "hint");
          return { ...next, hints: (next.hints ?? EMPTY_STATS.hints) + 1 };
        });
        notify(`Ad unavailable, +1 hint granted · ${Math.max(0, hintRewardsLeft - 1)} left`);
        return;
      }
      const result = await adapter.showRewarded("hint");
      if (!result.ok) {
        setStats((current) => {
          const next = recordRewardClaim(current, dateKey, "hint");
          return { ...next, hints: (next.hints ?? EMPTY_STATS.hints) + 1 };
        });
        notify(`Ad unavailable, +1 hint granted · ${Math.max(0, hintRewardsLeft - 1)} left`);
        return;
      }
      setStats((current) => {
        const next = recordRewardClaim(current, dateKey, "hint");
        return { ...next, hints: (next.hints ?? EMPTY_STATS.hints) + 1 };
      });
      notify(`+1 hint earned · ${Math.max(0, hintRewardsLeft - 1)} left`);
    } finally {
      setRewardBusy(null);
    }
  }

  async function watchRewardedNexus() {
    if (rewardBusy) return;
    if (nexusRewardsLeft <= 0) {
      notify("Nexus rewards are done for today");
      return;
    }

    setRewardBusy("nexus");
    if (adFree) {
      setStats((current) => {
        const next = recordRewardClaim(current, dateKey, "nexus");
        return { ...next, nexus: (next.nexus ?? EMPTY_STATS.nexus) + REWARDED_NEXUS };
      });
      notify(`Ad-free bonus: +${REWARDED_NEXUS} Nexus · ${Math.max(0, nexusRewardsLeft - 1)} left`);
      setRewardBusy(null);
      return;
    }

    notify("Starting rewarded ad...");
    try {
      const adapter = getAdsAdapter();
      const init = await adapter.initialize();
      if (!init.ok) {
        setStats((current) => {
          const next = recordRewardClaim(current, dateKey, "nexus");
          return { ...next, nexus: (next.nexus ?? EMPTY_STATS.nexus) + REWARDED_NEXUS };
        });
        notify(`Ad unavailable, +${REWARDED_NEXUS} Nexus granted · ${Math.max(0, nexusRewardsLeft - 1)} left`);
        return;
      }
      const result = await adapter.showRewarded("nexus");
      if (!result.ok) {
        setStats((current) => {
          const next = recordRewardClaim(current, dateKey, "nexus");
          return { ...next, nexus: (next.nexus ?? EMPTY_STATS.nexus) + REWARDED_NEXUS };
        });
        notify(`Ad unavailable, +${REWARDED_NEXUS} Nexus granted · ${Math.max(0, nexusRewardsLeft - 1)} left`);
        return;
      }
      setStats((current) => {
        const next = recordRewardClaim(current, dateKey, "nexus");
        return { ...next, nexus: (next.nexus ?? EMPTY_STATS.nexus) + REWARDED_NEXUS };
      });
      notify(`+${REWARDED_NEXUS} Nexus earned · ${Math.max(0, nexusRewardsLeft - 1)} left`);
    } finally {
      setRewardBusy(null);
    }
  }

  async function openAdPrivacyOptions() {
    notify("Opening privacy options...");
    const result = await getAdsAdapter().showPrivacyOptions();
    notify(result.message);
  }

  async function startDaily(level = activeLevel) {
    const savedGames = await webPlatform.load<Record<string, GameState>>("games", {});
    const nextGame = normalizeGame(savedGames[getDailyGameKey(level)], level);
    setActiveLevel(level);
    setSelectedMode("daily");
    setSavedGamesCache(savedGames);
    setGame(nextGame);
    setScreen("game");
    setModal(null);
    notify(
      nextGame.status !== "playing"
        ? `${DAILY_LEVELS[level].label} daily is already complete`
        : nextGame.guesses.length > 0
          ? `${DAILY_LEVELS[level].label} daily resumed`
          : `${DAILY_LEVELS[level].label} daily started`
    );
  }

  function openGameMenu() {
    setSelectedMode(game.mode);
    setActiveLevel(game.level ?? activeLevel);
    setScreen("menu");
    setModal(null);
    notify("Menu");
  }

  async function startUnlimited(level = activeLevel) {
    const savedGames = await webPlatform.load<Record<string, GameState>>("games", {});
    const lifeState = getUnlimitedLifeState(stats);
    if (lifeState.lives <= 0) {
      setModal("shop");
      notify(`No lives left. Wait ${lifeState.nextLifeText} for a free life or buy 3 lives now.`);
      return;
    }
    setActiveLevel(level);
    setSelectedMode("unlimited");
    setSavedGamesCache(savedGames);
    setGame(normalizeUnlimitedGame(savedGames.unlimited, level));
    setScreen("game");
    setModal(null);
    notify(`Unlimited ${DAILY_LEVELS[level].label} loaded`);
  }

  function startNextUnlimited(level = game.level) {
    const lifeState = getUnlimitedLifeState(stats);
    if (lifeState.lives <= 0 && game.status !== "won") {
      setModal("shop");
      notify(`No lives left. Wait ${lifeState.nextLifeText} for a free life or buy 3 lives now.`);
      return;
    }

    if (stageTransition) return;

    if (game.status === "won") {
      const nextStage = (stats.unlimitedCurrentRun ?? EMPTY_STATS.unlimitedCurrentRun) + 1;
      setStageTransition({ level, stage: nextStage });
      setModal(null);
      notify("Stage cleared");
      if (stageTimer.current) {
        window.clearTimeout(stageTimer.current);
      }
      stageTimer.current = window.setTimeout(() => {
        setActiveLevel(level);
        setGame(createUnlimitedGame(level));
        setStageTransition(null);
        notify(`Stage ${nextStage} ready`);
      }, 950);
      return;
    }

    setActiveLevel(level);
    setGame(createUnlimitedGame(level));
    setModal(null);
    notify("Next stage");
  }

  function startTesterTour() {
    const answer = "SPARK";
    const guesses = ["CLOUD", "SHARE", answer];
    const startedAt = Date.now() - 21000;
    const demoGame = createGame(`tester-tour-${Date.now()}`, answer, "unlimited", "classic");
    setActiveLevel("classic");
    setGame({
      ...demoGame,
      guesses,
      evaluations: guesses.map((guess) => evaluateGuess(guess, answer).map((item) => item.state)),
      status: "won",
      startedAt,
      completedAt: Date.now(),
      hintsUsed: 1,
      hint: "S is in position 1"
    });
    setStats((current) => ({
      ...current,
      wins: Math.max(current.wins, 4),
      currentStreak: Math.max(current.currentStreak, 3),
      maxStreak: Math.max(current.maxStreak, 5),
      nexus: Math.max(current.nexus ?? EMPTY_STATS.nexus, 260),
      hints: Math.max(current.hints ?? EMPTY_STATS.hints, 5),
      streakShields: Math.max(current.streakShields ?? EMPTY_STATS.streakShields, 2),
      ownedPerks: [...new Set([...(current.ownedPerks ?? EMPTY_STATS.ownedPerks), "theme-candy", "streak-shield"])],
      activeTheme: current.activeTheme ?? EMPTY_STATS.activeTheme
    }));
    setModal(null);
    notify("Tester tour ready");
  }

  const appClasses = [
    "app",
    activeTheme,
    screen === "menu" ? "is-menu" : game.status === "playing" ? "is-playing" : "is-complete",
    settings.colorblind ? "colorblind" : "",
    settings.highContrast ? "high-contrast" : ""
  ].join(" ");
  const outcomeText =
    game.status === "won"
      ? `You won in ${game.guesses.length}!`
      : game.status === "lost"
        ? `The word was ${game.answer}.`
        : "";
  const visibleDailyStreak = getVisibleDailyStreak(stats, dateKey);
  const dailyRows = getDailyRows(game, stats, leaderboards.daily, dateKey);
  const selectedDailyGame = normalizeGame(savedGamesCache[getDailyGameKey(activeLevel)], activeLevel);
  const selectedDailyStarted = selectedDailyGame.guesses.length > 0 || selectedDailyGame.currentGuess.length > 0;
  const selectedDailyComplete = selectedDailyGame.status !== "playing";

  return (
    <main className={appClasses}>
      <section className="game-shell" aria-label="Lexi Link game">
        <header className="topbar">
          <button className="icon-button" onClick={screen === "game" ? openGameMenu : () => setModal("help")} aria-label={screen === "game" ? "Back to menu" : "How to play"}>
            {screen === "game" ? <ListOrdered size={22} /> : <HelpCircle size={22} />}
          </button>
          <div className="brand">
            <span>Lexi Link</span>
            <small>
              {game.mode === "unlimited"
                ? `${levelConfig.label} Unlimited · ${formatLifeHearts(unlimitedLifeState.lives)}`
                : `${levelConfig.label} Daily #${puzzleNumber}`}
            </small>
          </div>
          <div className="top-actions">
            {TESTER_MODE && (
              <button className="icon-button" onClick={startTesterTour} aria-label="30-second tester tour">
                <Eye size={21} />
              </button>
            )}
            <button className="icon-button" onClick={() => setModal("leaders")} aria-label="Leaderboards">
              <Trophy size={22} />
            </button>
            <button className="icon-button" onClick={() => setModal("shop")} aria-label="Shop">
              <ShoppingBag size={22} />
            </button>
            <button className="icon-button" onClick={() => setModal("settings")} aria-label="Settings">
              <Settings size={22} />
            </button>
          </div>
        </header>

        <div className="boost-strip" aria-label="Progress">
          <button className="profile-chip" onClick={() => setModal("profile")} aria-label="Profile">
            <i style={{ background: stats.avatarColor ?? EMPTY_STATS.avatarColor }} />
            <span>{profileName}</span>
          </button>
          <div>
            {game.mode === "unlimited" ? <Heart size={17} /> : <Flame size={17} />}
            <span>{game.mode === "unlimited" ? formatLifeHearts(unlimitedLifeState.lives) : `${visibleDailyStreak} streak`}</span>
          </div>
          <div>
            <Sparkles size={17} />
            <span>{nexusPoints} nexus</span>
          </div>
          <div>
            {game.mode === "unlimited" ? <Flame size={17} /> : <ShieldCheck size={17} />}
            <span>{game.mode === "unlimited" ? `${stats.unlimitedCurrentRun ?? 0} run` : `${stats.streakShields ?? EMPTY_STATS.streakShields} shields`}</span>
          </div>
          <div>
            <Trophy size={17} />
            <span>{game.mode === "unlimited" ? `${stats.unlimitedWins ?? 0} wins` : `${stats.wins} wins`}</span>
          </div>
        </div>

        {screen === "menu" ? (
          <PreGameMenu
            selectedMode={selectedMode}
            selectedLevel={activeLevel}
            dailyGame={selectedDailyGame}
            dailyStarted={selectedDailyStarted}
            dailyComplete={selectedDailyComplete}
            puzzleNumber={puzzleNumber}
            stats={stats}
            unlimitedLifeState={unlimitedLifeState}
            onMode={setSelectedMode}
            onLevel={setActiveLevel}
            onStartDaily={() => startDaily(activeLevel)}
            onStartUnlimited={() => startUnlimited(activeLevel)}
            onShop={() => setModal("shop")}
            onStats={() => setModal("stats")}
          />
        ) : (
          <>
            <div className="game-mode-bar">
              <span>{game.mode === "daily" ? "Daily attempt saved" : "Unlimited run saved"}</span>
              <button onClick={openGameMenu}>
                <ListOrdered size={16} /> Menu
              </button>
            </div>

        <div className="daily-prompt">
          <strong>
            {game.mode === "unlimited"
              ? `${levelConfig.label} Unlimited: guess a ${wordLength}-letter word`
              : `${levelConfig.label}: guess today's ${wordLength}-letter word`}
          </strong>
          <span>
            {game.mode === "unlimited"
              ? `Win to keep your run. Lose and spend 1 life.`
              : `Green is right spot. Gold is in the word. ${maxRows} tries on this level.`}
          </span>
        </div>

        {game.status !== "playing" && (
          <section className="completion-panel" aria-live="polite">
            <div className={`status-strip ${game.status}`}>
              <Trophy size={18} />
              <span>{outcomeText}</span>
            </div>
            <ResultCard
              game={game}
              stats={stats}
              rows={dailyRows}
              shareRewardAvailable={shareRewardAvailable}
            />
            <div className="result-actions">
              {game.mode === "daily" && (
                <button className="primary-button next-stage-button play-unlimited-button" onClick={() => startUnlimited(game.level)}>
                  <Play size={18} /> Play {DAILY_LEVELS[game.level].label} Unlimited
                </button>
              )}
              {game.mode === "unlimited" && (
                <button className="primary-button next-stage-button" onClick={() => startNextUnlimited(game.level)} disabled={stageTransition !== null}>
                  {unlimitedLifeState.lives > 0 || game.status === "won" ? <ChevronsRight size={18} /> : <Heart size={18} />} {stageTransition ? "Loading..." : unlimitedLifeState.lives > 0 || game.status === "won" ? "Next stage" : "Refill lives"}
                </button>
              )}
              <button className="primary-button" onClick={() => setModal("share")}>
                <Share2 size={18} /> Share
              </button>
              <button className="secondary-button" onClick={() => setModal("stats")}>
                <BarChart3 size={18} /> Stats
              </button>
            </div>
          </section>
        )}

        <GameBoard
          game={game}
          invalidRow={invalidRow}
          rows={visibleRows}
          wordLength={wordLength}
          boardMetrics={boardMetrics}
          stageCleared={game.mode === "unlimited" && game.status === "won"}
        />

        {!adFree && <AdSlot placement="game" />}

        {game.status === "playing" && (
          <div className="status-strip" aria-live="polite">
            <span>
              {game.hint ?? (game.currentGuess.length === wordLength
                ? "Ready to score"
                : game.mode === "unlimited"
                  ? `${formatLifeHearts(unlimitedLifeState.lives)} · best stage ${stats.unlimitedBestRun ?? 0}`
                  : `Next reset ${resetAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`)}
            </span>
          </div>
        )}

        {game.status === "playing" && (
          <>
            {game.currentGuess.length === wordLength && (
              <button className="quick-submit" type="button" onClick={commitGuess}>
                Check word
              </button>
            )}
            <Keyboard states={keyboardStates} onPress={pressKey} onSubmit={commitGuess} readyToCheck={game.currentGuess.length === wordLength} />
          </>
        )}
          </>
        )}
      </section>

      {screen === "game" && game.status === "won" && <WinBurst />}
      {stageTransition && <StageTransition level={stageTransition.level} stage={stageTransition.stage} />}
      {toast && <div className="toast">{toast}</div>}

      {modal === "help" && <HelpModal level={activeLevel} onClose={() => setModal(null)} />}
      {modal === "stats" && <StatsModal game={game} stats={stats} onClose={() => setModal(null)} onShare={() => setModal("share")} onPractice={() => startUnlimited(activeLevel)} />}
      {modal === "leaders" && (
        <LeaderboardModal
          game={game}
          stats={stats}
          leaderboards={leaderboards}
          backendOnline={backendOnline}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "profile" && (
        <ProfileModal
          stats={stats}
          setStats={setStats}
          notify={notify}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "settings" && (
        <SettingsModal
          settings={settings}
          setSettings={setSettings}
          onClose={() => setModal(null)}
          onHint={revealHint}
          onRewardedHint={watchRewardedHint}
          onAdPrivacy={openAdPrivacyOptions}
          notify={notify}
          adFree={adFree}
          rewardedHintLeft={hintRewardsLeft}
          rewardBusy={rewardBusy}
        />
      )}
      {modal === "shop" && (
        <ShopModal
          stats={stats}
          setStats={setStats}
          ownedPerks={ownedPerks}
          dateKey={dateKey}
          onClose={() => setModal(null)}
          notify={notify}
          onRewardedHint={watchRewardedHint}
          onRewardedNexus={watchRewardedNexus}
          adFree={adFree}
          rewardedHintLeft={hintRewardsLeft}
          rewardedNexusLeft={nexusRewardsLeft}
          rewardBusy={rewardBusy}
          unlimitedLifeState={unlimitedLifeState}
        />
      )}
      {modal === "launch" && <LaunchModal backendOnline={backendOnline} onClose={() => setModal(null)} />}
      {modal === "share" && (
        <ShareModal
          game={game}
          stats={stats}
          puzzleNumber={puzzleNumber}
          onClose={() => setModal(null)}
          onNativeShare={shareResult}
          onCopy={() => copyShareText()}
          onNetwork={shareToNetwork}
        />
      )}
    </main>
  );
}

function PreGameMenu({
  selectedMode,
  selectedLevel,
  dailyGame,
  dailyStarted,
  dailyComplete,
  puzzleNumber,
  stats,
  unlimitedLifeState,
  onMode,
  onLevel,
  onStartDaily,
  onStartUnlimited,
  onShop,
  onStats
}: {
  selectedMode: PlayMode;
  selectedLevel: DailyLevel;
  dailyGame: GameState;
  dailyStarted: boolean;
  dailyComplete: boolean;
  puzzleNumber: number;
  stats: Stats;
  unlimitedLifeState: ReturnType<typeof getUnlimitedLifeState>;
  onMode: (mode: PlayMode) => void;
  onLevel: (level: DailyLevel) => void;
  onStartDaily: () => void;
  onStartUnlimited: () => void;
  onShop: () => void;
  onStats: () => void;
}) {
  const selectedConfig = DAILY_LEVELS[selectedLevel];
  const isDaily = selectedMode === "daily";
  const dailyButtonText = dailyComplete ? "Come back tomorrow" : dailyStarted ? "Resume Daily" : "Start Daily";
  const dailyStatus = dailyComplete
    ? `${selectedConfig.label} Daily #${puzzleNumber} is locked in. Come back tomorrow or play Unlimited.`
    : dailyStarted
      ? `${selectedConfig.label} Daily is in progress: ${
          dailyGame.currentGuess.length > 0 ? "typed word saved, " : ""
        }${dailyGame.guesses.length}/${selectedConfig.tries} tries used.`
      : `${selectedConfig.label} Daily starts only when you tap Start.`;
  const unlimitedStatus =
    unlimitedLifeState.lives > 0
      ? `${selectedConfig.label} Unlimited is ready. You get 1 free life.`
      : `${selectedConfig.label} Unlimited is out of lives. Wait ${unlimitedLifeState.nextLifeText} for a free life or buy 3 lives now.`;

  return (
    <section className="pre-game-menu" aria-label="Game menu">
      <div className="menu-hero">
        <small>Choose before the board opens</small>
        <strong>{isDaily ? `${selectedConfig.label} Daily #${puzzleNumber}` : `${selectedConfig.label} Unlimited`}</strong>
        <span>{isDaily ? dailyStatus : unlimitedStatus}</span>
      </div>

      <div className="menu-section" aria-label="Mode">
        <button className={isDaily ? "active" : ""} onClick={() => onMode("daily")}>
          <strong>Daily</strong>
          <span>One saved attempt</span>
        </button>
        <button className={!isDaily ? "active" : ""} onClick={() => onMode("unlimited")}>
          <strong>Unlimited</strong>
          <span>{formatLifeHearts(unlimitedLifeState.lives)}</span>
        </button>
      </div>

      <div className="menu-levels" role="tablist" aria-label="Difficulty">
        {DAILY_LEVEL_ORDER.map((level) => {
          const config = DAILY_LEVELS[level];
          const active = level === selectedLevel;
          return (
            <button className={active ? "active" : ""} onClick={() => onLevel(level)} key={level}>
              <strong>{config.label}</strong>
              <span>{config.wordLength} letters · {config.tries} tries</span>
            </button>
          );
        })}
      </div>

      <div className="menu-lock-note">
        <ShieldCheck size={18} />
        <span>Guesses and typed letters save as you play. Returning to menu will not reset a daily attempt.</span>
      </div>

      <div className="menu-actions">
        {isDaily ? (
          <>
            <button className="primary-button" onClick={dailyComplete ? undefined : onStartDaily} disabled={dailyComplete}>
              {dailyButtonText}
            </button>
            <button className="secondary-button" onClick={onStartUnlimited}>
              Try Unlimited
            </button>
          </>
        ) : (
          <>
            <button className="primary-button" onClick={unlimitedLifeState.lives > 0 ? onStartUnlimited : onShop}>
              {unlimitedLifeState.lives > 0 ? "Start Unlimited" : "Refill lives"}
            </button>
            <button className="secondary-button" onClick={() => onMode("daily")}>
              Daily Menu
            </button>
          </>
        )}
        <button className="secondary-button" onClick={onStats}>
          <BarChart3 size={18} /> Stats
        </button>
        <button className="secondary-button" onClick={onShop}>
          <ShoppingBag size={18} /> Shop
        </button>
      </div>

      <div className="menu-stats">
        <span>{getVisibleDailyStreak(stats, getLocalPuzzleDate())} daily streak</span>
        <span>{stats.unlimitedBestRun ?? 0} best stage</span>
        <span>{stats.nexus ?? EMPTY_STATS.nexus} nexus</span>
      </div>
    </section>
  );
}

function GameBoard({
  game,
  invalidRow,
  rows,
  wordLength,
  boardMetrics,
  stageCleared = false
}: {
  game: GameState;
  invalidRow: boolean;
  rows: number;
  wordLength: number;
  boardMetrics: { gap: number; tileSize: number; width: number };
  stageCleared?: boolean;
}) {
  const rowStyle = {
    gridTemplateColumns: `repeat(${wordLength}, ${boardMetrics.tileSize}px)`,
    gap: `${boardMetrics.gap}px`,
    width: `${boardMetrics.width}px`
  } as CSSProperties;

  const boardRows = Array.from({ length: rows }, (_, index) => {
    const submitted = game.guesses[index];
    const isCurrent = index === game.guesses.length;
    const isWinningRow = stageCleared && index === game.guesses.length - 1 && submitted === game.answer;
    const letters = submitted ?? (isCurrent ? game.currentGuess : "");
    const evaluation = submitted
      ? game.evaluations?.[index]?.map((state, letterIndex) => ({ letter: submitted[letterIndex], state })) ?? evaluateGuess(submitted, game.answer)
      : null;

    return (
      <div
        className={`board-row ${isCurrent && invalidRow ? "shake" : ""} ${isWinningRow ? "win-row" : ""}`}
        style={{ ...rowStyle, "--row-index": index } as CSSProperties}
        key={index}
      >
        {Array.from({ length: wordLength }, (_unused, letterIndex) => {
          const letter = letters[letterIndex] ?? "";
          const state = evaluation?.[letterIndex].state ?? "empty";
          return (
            <div
              className={`tile ${state} ${letter ? "filled" : ""}`}
              style={{ "--letter-index": letterIndex } as CSSProperties}
              key={letterIndex}
              aria-label={letter || "empty"}
            >
              <span>{letter}</span>
            </div>
          );
        })}
      </div>
    );
  });

  return (
    <div
      className={`board rows-${rows} letters-${wordLength} ${stageCleared ? "stage-cleared" : ""}`}
      style={
        {
          "--board-gap": `${boardMetrics.gap}px`,
          "--row-count": rows,
          "--tile-size": `${boardMetrics.tileSize}px`,
          "--word-length": wordLength,
          width: `${boardMetrics.width}px`
        } as CSSProperties
      }
    >
      {boardRows}
    </div>
  );
}

function WinBurst() {
  return (
    <div className="win-burst" aria-hidden="true">
      {Array.from({ length: 18 }, (_unused, index) => (
        <i key={index} style={{ "--burst-index": index } as React.CSSProperties} />
      ))}
    </div>
  );
}

function Keyboard({
  states,
  onPress,
  onSubmit,
  readyToCheck
}: {
  states: Map<string, string>;
  onPress: (key: string) => void;
  onSubmit: () => void;
  readyToCheck: boolean;
}) {
  return (
    <div className="keyboard" aria-label="Keyboard">
      {KEY_ROWS.map((row, rowIndex) => (
        <div className="key-row" key={row}>
          {rowIndex === 2 && (
            <button className={`key wide check-key ${readyToCheck ? "ready" : ""}`} type="button" onClick={onSubmit}>
              Check
            </button>
          )}
          {row.split("").map((letter) => (
            <button className={`key ${states.get(letter) ?? ""}`} type="button" key={letter} onClick={() => onPress(letter)}>
              {letter}
            </button>
          ))}
          {rowIndex === 2 && (
            <button className="key wide" type="button" onClick={() => onPress("Backspace")} aria-label="Backspace">
              <Delete size={17} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function HelpModal({ level, onClose }: { level: DailyLevel; onClose: () => void }) {
  const levelConfig = DAILY_LEVELS[level] ?? DAILY_LEVELS.classic;
  const sampleLetters = levelConfig.wordLength === 6 ? ["L", "I", "N", "K", "E", "D"] : ["L", "I", "N", "K", "S"];
  const howToPlay =
    level === "expert"
      ? "In Expert mode, you get 5 tries to guess the 6-letter word. Type a word, then tap Check."
      : `${levelConfig.label} gives you ${levelConfig.tries} tries to guess today's ${levelConfig.wordLength}-letter word. Type a word, then tap Check.`;

  return (
    <ModalFrame title="How to play" onClose={onClose}>
      <p>{howToPlay}</p>
      <div className="sample-row">
        {sampleLetters.map((letter, index) => (
          <div className={`tile ${index === 0 ? "correct" : index === 1 ? "present" : index === 2 ? "absent" : "empty"} filled`} key={`${letter}-${index}`}>
            {letter}
          </div>
        ))}
      </div>
      <p>Green means you have the right letter in the right spot. Gold means that letter is in the wrong spot. Dark means that letter is not in the word.</p>
    </ModalFrame>
  );
}

function StageTransition({ level, stage }: { level: DailyLevel; stage: number }) {
  return (
    <div className="stage-transition" aria-live="polite" aria-label="Stage transition">
      <div>
        <Sparkles size={28} />
        <small>{DAILY_LEVELS[level].label} Unlimited</small>
        <strong>Stage {stage}</strong>
        <span>New word loading</span>
      </div>
    </div>
  );
}

function ResultCard({
  game,
  stats,
  rows,
  shareRewardAvailable
}: {
  game: GameState;
  stats: Stats;
  rows: ReturnType<typeof getDailyRows>;
  shareRewardAvailable: boolean;
}) {
  const score = game.mode === "daily" ? getDailyScore(game, stats.currentStreak) : game.status === "won" ? 100 + (stats.unlimitedCurrentRun ?? 0) * 25 : 0;
  const maxGuesses = DAILY_LEVELS[game.level].tries;
  const rank = game.status === "won" ? rows.findIndex((row) => row.name === (stats.profileName || "You")) + 1 : rows.length + 1;
  const percentile = game.status === "won" ? Math.max(12, 100 - Math.round((rank / rows.length) * 100)) : 0;
  const isUnlimited = game.mode === "unlimited";

  return (
    <section className="result-card" aria-label="Daily result">
      <div>
        <small>{game.status === "won" ? (isUnlimited ? "Run Score" : `${DAILY_LEVELS[game.level].label} Score`) : isUnlimited ? "Run ended" : "Try again tomorrow"}</small>
        <strong>{game.status === "won" ? score : 0}</strong>
      </div>
      <div>
        <small>{isUnlimited ? "Run" : "Rank"}</small>
        <strong>{isUnlimited ? stats.unlimitedCurrentRun ?? 0 : game.status === "won" ? `#${rank}` : "-"}</strong>
      </div>
      <div>
        <small>{game.status === "won" ? (isUnlimited ? "Best" : "Beat") : "Answer"}</small>
        <strong>{game.status === "won" ? (isUnlimited ? stats.unlimitedBestRun ?? 0 : `${percentile}%`) : game.answer}</strong>
      </div>
      <span>
        {game.status === "won"
          ? `${game.guesses.length}/${maxGuesses} guesses, ${getElapsedSeconds(game)}s, ${game.hintsUsed} hints used`
          : isUnlimited
            ? "You spent 1 life, but earned a little Nexus."
            : "No score today, but you still earned a little Nexus."}
      </span>
      <em>
        {isUnlimited
          ? `${formatLifeHearts(stats.unlimitedLives ?? EMPTY_STATS.unlimitedLives)} ready`
          : shareRewardAvailable
            ? `Share today for +${SHARE_REWARD} Nexus`
            : "Share reward claimed for today"}
      </em>
    </section>
  );
}

function DailyGoals({ goals, onClaim }: { goals: DailyGoal[]; onClaim: (goal: DailyGoal) => void }) {
  const completeCount = goals.filter((goal) => goal.complete || goal.claimed).length;

  return (
    <section className="daily-goals" aria-label="Daily goals">
      <header>
        <div>
          <strong>Daily Goals</strong>
          <span>{completeCount}/{goals.length} ready</span>
        </div>
        <Sparkles size={18} />
      </header>
      <div className="goal-list">
        {goals.map((goal) => (
          <article className={`${goal.complete ? "complete" : ""} ${goal.claimed ? "claimed" : ""}`} key={goal.id}>
            <CheckCircle2 size={18} />
            <div>
              <strong>{goal.title}</strong>
              <small>{goal.detail}</small>
            </div>
            <button onClick={() => onClaim(goal)} disabled={!goal.complete || goal.claimed}>
              {goal.claimed ? "Claimed" : goal.complete ? `+${goal.reward}` : `+${goal.reward}`}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function AdSlot({ placement }: { placement: "game" | "result" | "shop" }) {
  const [status, setStatus] = useState("Loading sponsored ad...");
  const nativeAds = isNativeRuntime();

  useEffect(() => {
    let active = true;
    const adapter = getAdsAdapter();

    adapter.showSponsoredBanner(placement).then((result) => {
      if (!active) return;
      setStatus(result.ok ? "Sponsored ad" : result.message);
    });

    return () => {
      active = false;
      void adapter.hideSponsoredBanner();
    };
  }, [placement]);

  if (nativeAds) return null;

  return (
    <aside className={`ad-slot ${placement}`} aria-label={`${placement} sponsored placement`}>
      <span>Sponsored</span>
      <small>{status}</small>
      <i />
    </aside>
  );
}

function LeaderboardModal({
  game,
  stats,
  leaderboards,
  backendOnline,
  onClose
}: {
  game: GameState;
  stats: Stats;
  leaderboards: typeof EMPTY_LEADERBOARDS;
  backendOnline: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"daily" | "unlimited">("daily");
  const rows = tab === "daily" ? getDailyRows(game, stats, leaderboards.daily, getLocalPuzzleDate()) : getUnlimitedRows(stats, leaderboards.allTime);

  return (
    <ModalFrame title="Leaderboards" onClose={onClose}>
      <div className="tabs" role="tablist" aria-label="Leaderboard type">
        <button className={tab === "daily" ? "active" : ""} onClick={() => setTab("daily")}>
          Daily Streak
        </button>
        <button className={tab === "unlimited" ? "active" : ""} onClick={() => setTab("unlimited")}>
          Unlimited Stage
        </button>
      </div>
      <p className="leader-note">
        {tab === "daily"
          ? "Daily ranks correct-answer streaks. Miss a day and the streak starts over."
          : "Unlimited ranks the highest stage reached in one run."}
      </p>
      <div className="leader-list">
        {rows.map((row, index) => (
          <article className={`leader-row ${row.name === (stats.profileName || "You") ? "you" : ""}`} key={`${row.name}-${index}`}>
            <span className="rank">{index + 1}</span>
            <div>
              <strong>{row.name}</strong>
              <small>{tab === "daily" ? `${row.wins} daily wins` : `${row.wins} unlimited wins`}</small>
            </div>
            <b>{tab === "daily" ? `${row.streak} day${row.streak === 1 ? "" : "s"}` : `Stage ${row.score}`}</b>
          </article>
        ))}
      </div>
      <p className="shop-note">
        {backendOnline ? "Live Firebase leaderboard." : "Local preview data. Add Firebase env vars to make this live across players."}
      </p>
    </ModalFrame>
  );
}

function ShareModal({
  game,
  stats,
  puzzleNumber,
  onClose,
  onNativeShare,
  onCopy,
  onNetwork
}: {
  game: GameState;
  stats: Stats;
  puzzleNumber: number;
  onClose: () => void;
  onNativeShare: () => void;
  onCopy: () => void;
  onNetwork: (network: string, url: string) => void;
}) {
  const cardUrl = useMemo(() => makeShareCard(game, stats, puzzleNumber), [game, puzzleNumber, stats]);
  const encodedUrl = encodeURIComponent(SHARE_URL);
  const encodedText = encodeURIComponent("I played Lexi Link. Can you beat my score?");

  async function shareImage() {
    if (!cardUrl) return;
    const blob = await (await fetch(cardUrl)).blob();
    const file = new File([blob], `lexi-link-${puzzleNumber}.png`, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({
        title: "Lexi Link",
        text: "Can you beat my Lexi Link score?",
        files: [file]
      });
      return;
    }
    downloadImage();
  }

  function downloadImage() {
    if (!cardUrl) return;
    const link = document.createElement("a");
    link.href = cardUrl;
    link.download = `lexi-link-${puzzleNumber}.png`;
    link.click();
  }

  return (
    <ModalFrame title="Share Score" onClose={onClose}>
      {cardUrl && <img className="share-card-preview" src={cardUrl} alt="Lexi Link share card preview" />}
      <div className="modal-actions share-card-actions">
        <button className="primary-button" onClick={shareImage}>
          <ImageIcon size={18} /> Share Card
        </button>
        <button className="secondary-button" onClick={downloadImage}>
          <Download size={18} /> Download
        </button>
      </div>
      <div className="share-grid">
        <button onClick={onNativeShare}>
          <Share2 size={22} />
          <span>More</span>
        </button>
        <button onClick={() => onNetwork("Facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)}>
          <strong>f</strong>
          <span>Facebook</span>
        </button>
        <button onClick={() => onNetwork("Instagram", "https://www.instagram.com/")}>
          <Instagram size={22} />
          <span>Instagram</span>
        </button>
        <button onClick={() => onNetwork("TikTok", "https://www.tiktok.com/upload")}>
          <strong>♪</strong>
          <span>TikTok</span>
        </button>
        <button onClick={() => onNetwork("X", `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`)}>
          <X size={22} />
          <span>X</span>
        </button>
        <button onClick={() => onNetwork("WhatsApp", `https://wa.me/?text=${encodedText}%20${encodedUrl}`)}>
          <MessageCircle size={22} />
          <span>WhatsApp</span>
        </button>
        <button onClick={() => onNetwork("Threads", "https://www.threads.net/")}>
          <strong>@</strong>
          <span>Threads</span>
        </button>
        <button onClick={onCopy}>
          <Copy size={22} />
          <span>Copy</span>
        </button>
      </div>
      <p className="shop-note">Instagram, TikTok, and Threads open after copying your result so you can paste it into a post, story, or caption.</p>
    </ModalFrame>
  );
}

function StatsModal({
  game,
  stats,
  onClose,
  onShare,
  onPractice
}: {
  game: GameState;
  stats: Stats;
  onClose: () => void;
  onShare: () => void;
  onPractice: () => void;
}) {
  const winRate = stats.played ? Math.round((stats.wins / stats.played) * 100) : 0;
  const maxDistribution = Math.max(1, ...stats.distribution);
  const achievements = getAchievements(stats, game);

  return (
    <ModalFrame title="Stats" onClose={onClose}>
      <div className="stat-grid">
        <Stat label="Played" value={stats.played} />
        <Stat label="Win %" value={winRate} />
        <Stat label="Streak" value={stats.currentStreak} />
        <Stat label="Best" value={stats.maxStreak} />
        <Stat label="Shields" value={stats.streakShields ?? EMPTY_STATS.streakShields} />
        <Stat label="Lives" value={stats.unlimitedLives ?? EMPTY_STATS.unlimitedLives} />
      </div>
      <p className="stats-note">
        Streak Shields protect daily streaks. Unlimited mode gives 1 free life, recharges after 45 minutes, and sells 3-life packs for immediate play.
      </p>
      <div className="distribution">
        {stats.distribution.map((count, index) => (
          <div className="dist-row" key={index}>
            <span>{index + 1}</span>
            <div>
              <i style={{ width: `${Math.max(8, (count / maxDistribution) * 100)}%` }}>{count}</i>
            </div>
          </div>
        ))}
      </div>
      <div className="achievement-list">
        {achievements.map((achievement) => (
          <article className={`achievement ${achievement.unlocked ? "unlocked" : ""}`} key={achievement.id}>
            <Award size={18} />
            <div>
              <strong>{achievement.title}</strong>
              <small>{achievement.detail}</small>
            </div>
          </article>
        ))}
      </div>
      <div className="modal-actions">
        <button className="primary-button" onClick={onShare} disabled={game.status === "playing"}>
          <Share2 size={18} /> Share
        </button>
        <button className="secondary-button" onClick={onPractice}>
          <Play size={18} /> Unlimited
        </button>
      </div>
    </ModalFrame>
  );
}

function SettingsModal({
  settings,
  setSettings,
  onClose,
  onHint,
  onRewardedHint,
  onAdPrivacy,
  notify,
  adFree,
  rewardedHintLeft,
  rewardBusy
}: {
  settings: SettingsState;
  setSettings: Dispatch<SetStateAction<SettingsState>>;
  onClose: () => void;
  onHint: () => void;
  onRewardedHint: () => void | Promise<void>;
  onAdPrivacy: () => void;
  notify: (message: string) => void;
  adFree: boolean;
  rewardedHintLeft: number;
  rewardBusy: RewardedClaimKind | null;
}) {
  const toggle = (key: keyof SettingsState, label: string) => {
    setSettings((current) => {
      const next = { ...current, [key]: !current[key] };
      void webPlatform.save("settings", next);
      notify(`${label} ${next[key] ? "on" : "off"}`);
      return next;
    });
  };
  const rewardedHintLabel =
    rewardBusy === "hint"
      ? adFree
        ? "Claiming hint..."
        : "Playing ad..."
      : rewardedHintLeft > 0
      ? `${adFree ? "Claim daily hint bonus" : "Watch ad for hint"} · ${rewardedHintLeft} left`
      : "Hint rewards done today";

  return (
    <ModalFrame title="Settings" onClose={onClose}>
      <button className="setting-row" type="button" onClick={() => toggle("sound", "Sound")} aria-pressed={settings.sound}>
        <Volume2 size={20} />
        <span>Sound</span>
        <b>{settings.sound ? "On" : "Off"}</b>
      </button>
      <button className="setting-row" type="button" onClick={() => toggle("haptics", "Haptics")} aria-pressed={settings.haptics}>
        <Sparkles size={20} />
        <span>Haptics</span>
        <b>{settings.haptics ? "On" : "Off"}</b>
      </button>
      <button className="setting-row" type="button" onClick={() => toggle("colorblind", "Colorblind cues")} aria-pressed={settings.colorblind}>
        <Eye size={20} />
        <span>Colorblind cues</span>
        <b>{settings.colorblind ? "On" : "Off"}</b>
      </button>
      <button className="setting-row" type="button" onClick={() => toggle("highContrast", "High contrast")} aria-pressed={settings.highContrast}>
        <Eye size={20} />
        <span>High contrast</span>
        <b>{settings.highContrast ? "On" : "Off"}</b>
      </button>
      <button className="primary-button full" onClick={onHint}>
        <Sparkles size={18} /> Reveal hint
      </button>
      <button className="secondary-button full" onClick={onRewardedHint} disabled={rewardedHintLeft <= 0 || rewardBusy !== null}>
        <Sparkles size={18} /> {rewardedHintLabel}
      </button>
      <button className="secondary-button full" onClick={onAdPrivacy}>
        <Eye size={18} /> Ad privacy options
      </button>
      <p className="shop-note">Settings save instantly and apply to this device.</p>
    </ModalFrame>
  );
}

function ShopModal({
  stats,
  setStats,
  ownedPerks,
  dateKey,
  onClose,
  notify,
  onRewardedHint,
  onRewardedNexus,
  adFree,
  rewardedHintLeft,
  rewardedNexusLeft,
  rewardBusy,
  unlimitedLifeState
}: {
  stats: Stats;
  setStats: (stats: Stats | ((current: Stats) => Stats)) => void;
  ownedPerks: string[];
  dateKey: string;
  onClose: () => void;
  notify: (message: string) => void;
  onRewardedHint: () => void | Promise<void>;
  onRewardedNexus: () => void | Promise<void>;
  adFree: boolean;
  rewardedHintLeft: number;
  rewardedNexusLeft: number;
  rewardBusy: RewardedClaimKind | null;
  unlimitedLifeState: ReturnType<typeof getUnlimitedLifeState>;
}) {
  const balance = stats.nexus ?? EMPTY_STATS.nexus;
  const activeTheme = stats.activeTheme ?? EMPTY_STATS.activeTheme;
  const bonusClaimed = stats.lastBonusDate === dateKey;
  const purchaseAdapter = useMemo(() => getPurchaseAdapter(), []);
  const [busyProduct, setBusyProduct] = useState<PurchaseProductId | "restore" | null>(null);
  const [shopStatus, setShopStatus] = useState("Choose cash checkout or spend Nexus.");
  const rewardedHintLabel =
    rewardBusy === "hint"
      ? adFree
        ? "Claiming hint..."
        : "Playing ad..."
      : rewardedHintLeft > 0
      ? `${adFree ? "Claim bonus hint" : "Watch ad for +1 hint"} · ${rewardedHintLeft} left`
      : "Hint rewards done today";
  const rewardedNexusLabel =
    rewardBusy === "nexus"
      ? adFree
        ? `Claiming +${REWARDED_NEXUS} Nexus...`
        : "Playing ad..."
      : rewardedNexusLeft > 0
      ? `${adFree ? `Claim +${REWARDED_NEXUS} Nexus` : `Watch ad for +${REWARDED_NEXUS} Nexus`} · ${rewardedNexusLeft} left`
      : "Nexus rewards done today";

  function showShopStatus(message: string) {
    setShopStatus(message);
    notify(message);
  }

  function claimDailyBonus() {
    if (bonusClaimed) {
      showShopStatus("Daily bonus already claimed");
      return;
    }

    setStats((current) => ({
      ...current,
      nexus: (current.nexus ?? EMPTY_STATS.nexus) + 25,
      lastBonusDate: dateKey
    }));
    showShopStatus("+25 Nexus claimed");
  }

  function buyWithNexus(item: (typeof SHOP_ITEMS)[number]) {
    const owned = ownedPerks.includes(item.id);
    const product = PRODUCT_CATALOG[item.productId];

    if (item.kind === "theme" && owned) {
      setStats((current) => ({ ...current, activeTheme: item.id }));
      showShopStatus("Theme equipped");
      return;
    }

    if (item.kind === "unlock" && owned) {
      showShopStatus("Already owned");
      return;
    }

    if (balance < item.price) {
      showShopStatus("Not enough Nexus");
      return;
    }

    setStats((current) => {
      const currentOwned = current.ownedPerks ?? EMPTY_STATS.ownedPerks;
      const base = {
        ...current,
        nexus: (current.nexus ?? EMPTY_STATS.nexus) - item.price,
        ownedPerks: item.kind === "consumable" ? currentOwned : [...new Set([...currentOwned, item.id])]
      };
      return applyProductGrant(base, { ...product, grant: { ...product.grant, nexus: 0 } });
    });
    showShopStatus(item.productId === "life_refill" ? "+3 Unlimited lives added" : item.kind === "theme" ? "Theme unlocked" : "Perk purchased");
  }

  async function buyWithStore(item: (typeof SHOP_ITEMS)[number]) {
    const owned = ownedPerks.includes(item.id);
    if (item.kind !== "consumable" && owned) {
      showShopStatus("Already owned");
      return;
    }

    setBusyProduct(item.productId);
    setShopStatus(`Opening checkout for ${item.title}...`);
    try {
      const result = await purchaseAdapter.purchase(item.productId);
      if (!result.ok || !result.product) {
        showShopStatus(result.message);
        return;
      }
      setStats((current) => applyPurchaseGrant(current, result.product!, result.transactionId ?? `${result.platform}-${Date.now()}`, result.platform));
      showShopStatus(result.product.id === "life_refill" ? "+3 Unlimited lives added" : result.product.type === "consumable" ? result.message : `${result.product.title} unlocked`);
    } catch {
      showShopStatus("Purchase could not be completed");
    } finally {
      setBusyProduct(null);
    }
  }

  async function restorePurchases() {
    setBusyProduct("restore");
    setShopStatus("Checking store purchases...");
    try {
      const result = await purchaseAdapter.restore();
      if (result.products.length === 0) {
        showShopStatus(result.message);
        return;
      }
      setStats((current) =>
        result.products.reduce(
          (nextStats, product) => applyPurchaseGrant(nextStats, product, `restore-${product.id}-${Date.now()}`, result.platform),
          current
        )
      );
      showShopStatus(`${result.products.length} purchase${result.products.length === 1 ? "" : "s"} restored`);
    } catch {
      showShopStatus("Restore failed");
    } finally {
      setBusyProduct(null);
    }
  }

  async function claimRewardedHint() {
    setShopStatus(adFree ? "Claiming bonus hint..." : "Starting rewarded ad...");
    await onRewardedHint();
  }

  async function claimRewardedNexus() {
    setShopStatus(adFree ? `Claiming +${REWARDED_NEXUS} Nexus...` : "Starting rewarded ad...");
    await onRewardedNexus();
  }

  return (
    <ModalFrame title="Perk Shop" onClose={onClose}>
      <div className="wallet">
        <Sparkles size={19} />
        <strong>{balance} Nexus</strong>
        <span>
          {stats.hints ?? EMPTY_STATS.hints} hints · {formatLifeHearts(unlimitedLifeState.lives)}
        </span>
      </div>
      <div className="life-panel">
        <Heart size={19} />
        <div>
          <strong>Unlimited Lives</strong>
          <span>
            {unlimitedLifeState.lives >= MAX_UNLIMITED_LIVES
              ? `${formatLifeBalance(unlimitedLifeState.lives)} ready · buy 3 extra lives anytime`
              : `${formatLifeHearts(unlimitedLifeState.lives)} ready · next free life in ${unlimitedLifeState.nextLifeText} · buy 3 now`}
          </span>
        </div>
      </div>
      <p className="shop-status" aria-live="polite">{shopStatus}</p>
      <div className="store-mode">
        <span>Secure checkout</span>
        <button className="restore-button" onClick={restorePurchases} disabled={busyProduct !== null}>
          {busyProduct === "restore" ? "Restoring" : "Restore"}
        </button>
      </div>
      <button className={`daily-bonus ${bonusClaimed ? "claimed" : ""}`} onClick={claimDailyBonus}>
        <span>Daily Bonus</span>
        <strong>{bonusClaimed ? "Claimed" : "+25 Nexus"}</strong>
      </button>
      <div className="ad-rewards">
        <button onClick={claimRewardedHint} disabled={rewardedHintLeft <= 0 || rewardBusy !== null}>
          <Sparkles size={18} />
          <span>{rewardedHintLabel}</span>
        </button>
        <button onClick={claimRewardedNexus} disabled={rewardedNexusLeft <= 0 || rewardBusy !== null}>
          <Gem size={18} />
          <span>{rewardedNexusLabel}</span>
        </button>
      </div>
      {!adFree && <AdSlot placement="shop" />}
      <div className="shop-list">
        {SHOP_ITEMS.map((item) => {
          const Icon = item.icon;
          const owned = ownedPerks.includes(item.id);
          const equipped = activeTheme === item.id;
          const product = PRODUCT_CATALOG[item.productId];
          const nexusLabel = item.kind === "theme" && owned ? (equipped ? "Equipped" : "Equip") : owned ? "Owned" : `Use ${item.price}`;
          const storeLabel = busyProduct === item.productId ? "Working" : product.priceLabel;

          return (
            <article className={`shop-item ${equipped ? "equipped" : ""}`} key={item.id}>
              <div className="shop-icon">
                <Icon size={22} />
              </div>
              <div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <small>{item.kind === "consumable" ? "Bonus content" : item.kind === "theme" ? "Visual theme" : "Premium pass"}</small>
              </div>
              <div className="shop-actions">
                <button className="buy-button cash" onClick={() => buyWithStore(item)} disabled={busyProduct !== null || (item.kind !== "consumable" && owned)}>
                  {item.kind !== "consumable" && owned ? "Owned" : storeLabel}
                </button>
                <button className="buy-button" onClick={() => buyWithNexus(item)} disabled={busyProduct !== null || equipped || (item.kind === "unlock" && owned)}>
                  {nexusLabel}
                </button>
              </div>
            </article>
          );
        })}
      </div>
      <p className="shop-note">
        {adFree ? "Ad-Free Pass is active. Reward buttons grant the same bonuses without showing ads." : "Rewarded ads are optional and grant hints or Nexus after completion."}
      </p>
    </ModalFrame>
  );
}

function LaunchModal({ backendOnline, onClose }: { backendOnline: boolean; onClose: () => void }) {
  const checklist = getLaunchChecklist(backendOnline);
  const completed = checklist.filter((item) => item.done).length;
  const percent = Math.round((completed / checklist.length) * 100);

  return (
    <ModalFrame title="Launch Prep" onClose={onClose}>
      <section className="launch-summary">
        <Rocket size={24} />
        <div>
          <strong>{percent}% ready</strong>
          <span>
            {completed} of {checklist.length} launch checks complete
          </span>
        </div>
      </section>
      <div className="launch-list">
        {checklist.map((item) => (
          <article className={item.done ? "done" : ""} key={item.title}>
            {item.done ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
            <div>
              <strong>{item.title}</strong>
              <small>{item.detail}</small>
            </div>
          </article>
        ))}
      </div>
      <div className="launch-links">
        <code>/privacy.html</code>
        <code>/support.html</code>
        <code>STORE_LISTING_COPY.md</code>
        <code>PRIVACY_AND_DATA_FORMS.md</code>
        <code>ADS_SETUP.md</code>
        <code>IAP_SETUP.md</code>
        <code>BACKEND_SETUP.md</code>
        <code>MOBILE_BUILD.md</code>
      </div>
      <p className="shop-note">This panel tracks what the app can know locally. Apple, Google, and RevenueCat dashboard steps still need manual confirmation.</p>
    </ModalFrame>
  );
}

function ProfileModal({
  stats,
  setStats,
  notify,
  onClose
}: {
  stats: Stats;
  setStats: (stats: Stats | ((current: Stats) => Stats)) => void;
  notify: (message: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(stats.profileName || EMPTY_STATS.profileName);
  const [avatarColor, setAvatarColor] = useState(stats.avatarColor || EMPTY_STATS.avatarColor);

  function saveProfile() {
    const cleanName = name.replace(/[^\w .'-]/g, "").trim().slice(0, 24);
    if (!cleanName) {
      notify("Name cannot be blank");
      return;
    }

    setStats((current) => ({
      ...current,
      profileName: cleanName,
      avatarColor
    }));
    notify("Profile saved");
    onClose();
  }

  return (
    <ModalFrame title="Profile" onClose={onClose}>
      <div className="profile-preview">
        <i style={{ background: avatarColor }} />
        <strong>{name.trim() || "Player"}</strong>
        <span>Leaderboard name</span>
      </div>
      <label className="profile-field">
        <span>Display name</span>
        <input value={name} maxLength={24} onChange={(event) => setName(event.target.value)} />
      </label>
      <div className="color-picker" aria-label="Avatar color">
        {AVATAR_COLORS.map((color) => (
          <button
            className={avatarColor === color ? "active" : ""}
            key={color}
            onClick={() => setAvatarColor(color)}
            aria-label={`Avatar color ${color}`}
          >
            <i style={{ background: color }} />
          </button>
        ))}
      </div>
      <button className="primary-button full" onClick={saveProfile}>
        <User size={18} /> Save Profile
      </button>
    </ModalFrame>
  );
}

function ModalFrame({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}
